import { ReactNode, useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { hasTabAccess } from "@/lib/adminPermissionMap";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import { savePendingDeepLink } from "@/lib/notifications/pendingDeepLink";

/**
 * Protects /voice/* with plain Supabase auth, then allows either HIBANE Desk
 * users with voice-agent access or tenant users with an active
 * voice_org_members row. Tenant users deliberately do not need an
 * admin_sessions row.
 *
 * Perpetual-session contract (mirrors AdminProtectedRoute):
 * On PWA cold-start Supabase can report `loading=false, user=null` for a beat
 * before rehydrating from localStorage / minting a fresh access token. We
 * therefore never bounce to /voice/login on a transient null user — instead
 * we run an 8s grace loop that (a) subscribes to onAuthStateChange, (b) polls
 * getSession(), and (c) fires one silent refreshSession(). Only if the grace
 * window fully elapses without a user do we redirect. Storage is never
 * cleared here; only explicit Sign Out ends a tenant session.
 */
export default function VoiceProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, loading } = useAdminAuth();
  const [rehydrating, setRehydrating] = useState(false);
  const [rehydrateFailed, setRehydrateFailed] = useState(false);

  if (location.pathname.startsWith("/voice") && location.pathname !== "/voice/login") {
    savePendingDeepLink("voice", location.pathname + location.search);
  }

  // Cold-start grace: when auth finishes loading with no user, wait for
  // Supabase to rehydrate before deciding the tenant is signed out.
  //
  // Persistent-session contract: if the Supabase refresh token is present in
  // localStorage (sb-<ref>-auth-token), we NEVER redirect to /voice/login on
  // a transient null user — we keep the spinner up until an auth event
  // resolves (or explicit Sign Out clears the token). Only when storage
  // itself is empty do we bounce after a 15s grace.
  //
  // Also reset rehydrateFailed if a later SIGNED_IN/TOKEN_REFRESHED/
  // INITIAL_SESSION fires with a real user (mounted listener below).
  useEffect(() => {
    // Global auth listener that always resets rehydrateFailed on a real user.
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      if (
        sess?.user &&
        (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION")
      ) {
        setRehydrateFailed(false);
        setRehydrating(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const hasPersistedSupabaseSession = useCallback(() => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
          const raw = localStorage.getItem(k);
          if (raw && raw.length > 10) return true;
        }
      }
    } catch { /* noop */ }
    return false;
  }, []);

  useEffect(() => {
    if (loading || user) {
      if (user && rehydrateFailed) setRehydrateFailed(false);
      return;
    }
    if (rehydrating || rehydrateFailed) return;

    // If there's no persisted Supabase token, there's nothing to rehydrate —
    // redirect to /voice/login immediately instead of spinning for 15s.
    if (!hasPersistedSupabaseSession()) {
      setRehydrateFailed(true);
      return;
    }

    setRehydrating(true);
    let cancelled = false;
    const start = Date.now();
    const graceMs = 15000;
    let refreshTried = false;


    const poll = async () => {
      while (!cancelled) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          cancelled = true;
          setRehydrating(false);
          return;
        }
        if (!refreshTried && Date.now() - start > 400) {
          refreshTried = true;
          await supabase.auth.refreshSession().catch(() => {});
        }
        // Backoff: 250ms early, then 500ms.
        const elapsed = Date.now() - start;
        await new Promise((r) => setTimeout(r, elapsed < 3000 ? 250 : 500));

        if (Date.now() - start >= graceMs) {
          // Grace elapsed. If a Supabase refresh token still lives in
          // localStorage, keep the spinner up indefinitely — the auth
          // listener above will resolve it (or the user explicitly signs
          // out). Only bounce when storage is truly empty.
          if (hasPersistedSupabaseSession()) {
            // Extend another grace window; try one more refresh.
            refreshTried = false;
            await supabase.auth.refreshSession().catch(() => {});
            await new Promise((r) => setTimeout(r, 1000));
            continue;
          }
          setRehydrating(false);
          setRehydrateFailed(true);
          return;
        }
      }
    };
    void poll();

    return () => { cancelled = true; };
  }, [loading, user, rehydrating, rehydrateFailed, hasPersistedSupabaseSession]);

  if (loading || rehydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user && rehydrateFailed) {
    return <Navigate to="/voice/login" replace />;
  }

  if (!user) {
    // Grace loop hasn't started/finished yet — keep spinner up.
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <VoiceAccessGate>{children}</VoiceAccessGate>
  );
}

function VoiceAccessGate({ children }: { children: ReactNode }) {
  const { isSuperAdmin, permissions, loading, initialized } = useAdminPermissions();
  const { user, isAdmin, clearAdminSession } = useAdminAuth();

  // React-query-backed membership check so mutations on voice_org_members can
  // invalidate ["voice-access"] and have the UI re-evaluate without a reload.
  const { data: orgMemberCheck, isLoading: memberLoading } = useQuery({
    queryKey: ["voice-access", user?.id ?? null],
    staleTime: 60_000,
    queryFn: async (): Promise<"member" | "none"> => {
      const { data: u } = await supabase.auth.getUser();
      if (!u?.user) return "none";
      const { data, error } = await supabase
        .from("voice_org_members")
        .select("id")
        .eq("user_id", u.user.id)
        .eq("status", "active")
        .limit(1);
      if (error) return "none";
      return data && data.length > 0 ? "member" : "none";
    },
  });

  const waitingForDeskPermissions = isAdmin && (loading || !initialized);
  if (waitingForDeskPermissions || memberLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const allowedAsDesk = hasTabAccess("voice-agent", isSuperAdmin, permissions);
  const allowedAsOrgMember = orgMemberCheck === "member";
  if (allowedAsDesk || allowedAsOrgMember) return <>{children}</>;

  const signOut = async () => {
    try { await supabase.auth.signOut(); } catch { /* noop */ }
    clearAdminSession();
    window.location.replace("/voice/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full rounded-2xl border bg-card p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-foreground">No Voice Agent access</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Your account ({user?.email}) is signed in but isn't a member of any
          Voice organization. Ask your organization admin (or a HIBANE
          administrator) to grant you access.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={signOut} variant="default" className="w-full">Sign out</Button>
        </div>
      </div>
    </div>
  );
}

export { VoiceProtectedRoute };
